import { useEffect } from 'react'
import { Bell, Check, CheckCheck, Loader2 } from 'lucide-react'
import { useNotificationStore } from '@/store/notificationStore'

export function NotificationsPage() {
  const { notifications, unreadCount, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore()

  useEffect(() => {
    fetchNotifications()
  }, [])

  const formatTime = (ts: string) => {
    const d = new Date(ts)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins} min ago`
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
    return d.toLocaleDateString()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsRead()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-[#1a1a2e] rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Mark all as read
          </button>
        )}
      </div>

      {isLoading && notifications.length === 0 ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-xl border-2 border-[#1a1a2e]">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 bg-white rounded-xl border-2 border-[#1a1a2e]">
          <Bell className="w-12 h-12 text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No notifications yet</p>
          <p className="text-sm text-gray-400 mt-1">Notifications from escalations and checkpoints will appear here</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] divide-y divide-gray-100">
          {notifications.map((notif) => (
            <div
              key={notif.id}
              className={`flex items-start gap-4 p-4 transition-colors ${!notif.read ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}
            >
              <div className="mt-1">
                {!notif.read ? (
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                ) : (
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className={`text-sm ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {notif.title}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">{notif.message}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400">{formatTime(notif.created_at)}</span>
                    {!notif.read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="p-1 hover:bg-blue-100 rounded transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4 text-blue-500" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    notif.type === 'escalation' ? 'bg-red-100 text-red-700' :
                    notif.type === 'checkpoint' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {notif.type}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default NotificationsPage
